import { useState, useEffect, useMemo, useCallback } from 'react';
import Fuse, { FuseResult, FuseResultMatch } from 'fuse.js';
import { ShopifyProduct } from '@/hooks/useSelectiveShopify';

interface SearchResult {
  item: ShopifyProduct;
  score?: number;
  matches?: readonly FuseResultMatch[];
}

interface UseFuzzySearchOptions {
  threshold?: number;
  keys?: (string | { name: string; weight: number })[];
  includeScore?: boolean;
  includeMatches?: boolean;
  minMatchCharLength?: number;
}

const defaultOptions: UseFuzzySearchOptions = {
  threshold: 0.2, // More strict default for surgical precision
  keys: [
    { name: 'title', weight: 0.4 },
    { name: 'description', weight: 0.2 },
    { name: 'productType', weight: 0.3 },
    { name: 'tags', weight: 0.1 }
  ],
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 2
};

export const useFuzzySearch = (products: ShopifyProduct[], options: UseFuzzySearchOptions = {}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const fuseOptions = useMemo(() => ({ ...defaultOptions, ...options }), [options]);
  
  const fuse = useMemo(() => {
    return new Fuse(products, fuseOptions);
  }, [products, fuseOptions]);

  const search = useCallback((query: string) => {
    setIsSearching(true);
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Fashion-specific terms that need exact matching
    const exactMatchTerms = ['midi', 'maxi', 'mini', 'crop', 'high-waisted', 'low-rise'];
    const queryWords = query.toLowerCase().split(/\s+/);
    
    // Check if query contains exact match terms
    const hasExactTerms = queryWords.some(word => exactMatchTerms.includes(word));
    
    if (hasExactTerms) {
      // Use exact word boundary matching for precise fashion terms
      const exactResults = products.filter(product => {
        const searchText = [
          product.title,
          product.description,
          product.productType,
          ...(product.tags || [])
        ].join(' ').toLowerCase();
        
        return queryWords.every(word => {
          if (exactMatchTerms.includes(word)) {
            // Exact word boundary match for fashion terms
            const regex = new RegExp(`\\b${word}\\b`, 'i');
            return regex.test(searchText);
          } else {
            // Fuzzy match for other words
            return searchText.includes(word);
          }
        });
      }).map(item => ({ item, score: 0, matches: [] }));
      
      setSearchResults(exactResults as SearchResult[]);
    } else {
      // Use fuzzy search for general queries
      const results = fuse.search(query);
      setSearchResults(results as SearchResult[]);
    }
    
    setIsSearching(false);
  }, [fuse, products]);

  const getSearchSuggestions = useCallback((query: string, limit: number = 5): string[] => {
    if (!query.trim() || query.length < 2) return [];

    const results = fuse.search(query, { limit: limit * 2 });
    const suggestions = new Set<string>();

    results.forEach(result => {
      // Extract relevant suggestions from matches
      if (result.matches) {
        result.matches.forEach(match => {
          if (match.key === 'title' || match.key === 'productType') {
            suggestions.add(result.item[match.key as keyof ShopifyProduct] as string);
          }
          if (match.key === 'tags' && Array.isArray(result.item.tags)) {
            result.item.tags.forEach(tag => {
              if (tag.toLowerCase().includes(query.toLowerCase())) {
                suggestions.add(tag);
              }
            });
          }
        });
      }
    });

    return Array.from(suggestions).slice(0, limit);
  }, [fuse]);

  const highlightMatches = useCallback((text: string, matches?: readonly FuseResultMatch[]): string => {
    if (!matches) return text;

    let highlightedText = text;
    matches.forEach(match => {
      if (match.indices) {
        // Sort indices in reverse order to avoid index shifting
        const sortedIndices = [...match.indices].sort((a, b) => b[0] - a[0]);
        sortedIndices.forEach(([start, end]) => {
          const before = highlightedText.slice(0, start);
          const highlighted = highlightedText.slice(start, end + 1);
          const after = highlightedText.slice(end + 1);
          highlightedText = `${before}<mark class="bg-primary/20 font-medium">${highlighted}</mark>${after}`;
        });
      }
    });

    return highlightedText;
  }, []);

  // Clear search results
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  // Helper to get just the products from search results
  const getFilteredProducts = useCallback(() => searchResults.map(result => result.item), [searchResults]);

  return {
    searchQuery,
    searchResults,
    isSearching,
    search,
    clearSearch,
    getSearchSuggestions,
    highlightMatches,
    getFilteredProducts
  };
};